import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    BudgetProposalSettingsT,
    BudgetProposalT,
} from 'routes/BudgetModule/config/types';
import { debounce } from 'lodash';
import {
    ACTIONS,
    TRACKABLE_EVENTS,
    trackEventBudget,
} from 'helpers/trackerHelpers';
import {
    useExportInscriptionsExcel,
    useExportInscriptionsExcelForReimport,
} from 'routes/BudgetModule/helpers/export';
import GetAppIcon from '@mui/icons-material/GetApp';
import { ButtonMenu } from 'components/ButtonMenu';
import SearchBar from 'ui/SearchBar/SearchBar';
import { isActiveBudgetPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import StyActionContainer from 'ui/StyledComponents/StyActionContainer';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import ToggleWithTooltip from 'ui/ToggleButton/ToggleWithTooltip';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

const StyZoomButtonSearchBarContainer = styled('div', {
    name: 'StyZoomButtonSearchBarContainer',
})({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: '10px',
    gap: '20px',
});

type TabActionsImputationsProps = {
    budgetProposal: BudgetProposalT;
    inscriptionsData: { columns: ResponseColumnT[]; data: InscriptionT[] };
    showEmptyBudgetLines: boolean;
    setFilterInscriptions: FlowAnyFunction;
    setShowEmptyBudgetLines: FlowAnyFunction;
    onSettingsEdit: (arg1: Partial<BudgetProposalSettingsT>) => void;
    exportFunction: () => void;
};

const StyButtonMenu = styled(ButtonMenu, {
    name: 'StyButtonMenu',
})({
    height: '26px',
    minHeight: '26px',
});

const DEBOUNCE_TIME = 1000;

export function TabActionsImputations({
    budgetProposal,
    inscriptionsData,
    showEmptyBudgetLines,
    setFilterInscriptions: setFilterInscriptionsProp,
    setShowEmptyBudgetLines,
    onSettingsEdit,
    exportFunction,
}: TabActionsImputationsProps): React.ReactElement {
    const showEmptyBudgetLinesToggler = isActiveBudgetPhase(
        budgetProposal.phase,
    );
    const { t } = useTranslation();
    const { projects } = useGetProjects();
    const statusesSettings = useContext(StatusSettingsContext);
    const mutateSettings = useCallback(
        debounce(onSettingsEdit, DEBOUNCE_TIME),
        [onSettingsEdit],
    );

    const trackingMetadata = {
        budgetProposal: budgetProposal.name,
        phase: t(`budgetProposal.${budgetProposal.phase}`),
    };

    const exportGrid = () => {
        exportFunction();
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                .exportExcel,
            trackingMetadata,
        );
    };
    const secondaryBudgetAxisSourceTable =
        useSelectSecondaryBudgetAxisSourceTable();
    const exportInscriptionsExcelForReimport =
        useExportInscriptionsExcelForReimport({
            inscriptionsData,
            phase: budgetProposal.phase,
            statusesSettings,
            trackEventName:
                TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                    .exportExcel,
            trackingMetadata,
            secondaryBudgetAxisSourceTable,
            exercice: budgetProposal.exercice,
        });

    const exportInscriptionsExcel = useExportInscriptionsExcel({
        inscriptionsData,
        phase: budgetProposal.phase,
        statusesSettings,
        projects,
        trackEventName:
            TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                .exportExcel,
        trackingMetadata,
        exercice: budgetProposal.exercice,
    });

    const exportMenuItems = [
        {
            name: t('budgetProposal.exportInscriptionsExcel'),
            action: exportInscriptionsExcel,
        },
        {
            name: t('budgetProposal.exportInscriptiontForReimport'),
            action: exportInscriptionsExcelForReimport,
        },
        {
            name: t('budgetProposal.exportBudgetLines'),
            action: exportGrid,
        },
    ];

    const setFilterInscriptions = (queryStr: string) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                .labelSearch,
            trackingMetadata,
        );
        setFilterInscriptionsProp(queryStr);
    };

    const onHideEmptyBudgetLinesChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                .toggleEmpty,
            {
                budgetProposal: budgetProposal.name,
                phase: t(`budgetProposal.${budgetProposal.phase}`),
                action: event.target.checked ? ACTIONS.hide : ACTIONS.show,
            },
        );
        setShowEmptyBudgetLines(!event.target.checked);

        mutateSettings({
            showEmptyBudgetLines: !event.target.checked,
        });
    };

    return (
        <StyActionContainer>
            <StyZoomButtonSearchBarContainer>
                <ConditionnalZoomIconButton />
                <SearchBar
                    handleChange={setFilterInscriptions}
                    width="17rem"
                    placeholder={t('budget.inscriptionSearchBar')}
                />
            </StyZoomButtonSearchBarContainer>

            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {showEmptyBudgetLinesToggler && (
                    <ToggleWithTooltip
                        title={t('budgetProposal.hideEmptyBudgetLines')}
                        tooltipTitle={t(
                            'budgetProposal.hideEmptyBudgetLinesTooltip',
                        )}
                        isChecked={!showEmptyBudgetLines}
                        onChange={onHideEmptyBudgetLinesChange}
                    />
                )}

                <StyButtonMenu
                    iconElement={<GetAppIcon />}
                    buttonLabel={t('budgetProposal.export')}
                    menuItems={exportMenuItems}
                    extra={{
                        variant: 'contained',
                        color: 'primary',
                    }}
                />
            </Box>
        </StyActionContainer>
    );
}
